/** The file every Helm chart must have at its root. */
const CHART_FILE = 'Chart.yaml';

/**
 * Reports whether a path in the source is a Helm chart directory.
 *
 * Presence of Chart.yaml is how Helm itself decides - `chartutil.IsChartDir`
 * checks exactly this, and the loader fails without it - and it is the only
 * thing that can be checked: a chart's templates are Go templates rather than
 * manifests, so reading the files tells you nothing.
 */
export function isChartDirectory(
  paths: Array<string>,
  directory: string
): boolean {
  const chartFile = `${trimSlashes(directory)}/${CHART_FILE}`;

  return paths.some((path) => trimSlashes(path) === chartFile);
}

/**
 * The values files that can be merged over a chart's own values.
 *
 * Scoped to the chart's own directory, because that is where a chart's
 * alternative values normally sit and the rest of a source is mostly files that
 * are not values at all. A repository that keeps its values elsewhere widens
 * the search instead.
 *
 * The chart's own values.yaml is left out because Helm reads it as the base
 * these are layered over - which is what makes them overrides, and matches
 * `helm -f` and Argo CD's valueFiles rather than Flux's, where naming any
 * values file replaces the chart's defaults instead of adding to them. Its
 * templates are left out because they are Go templates rather than values.
 */
export function chartValuesFiles(
  paths: Array<string>,
  directory: string,
  { wholeSource = false }: { wholeSource?: boolean } = {}
): Array<string> {
  const root = trimSlashes(directory);

  function inChart(path: string) {
    return path.startsWith(`${root}/`);
  }

  return paths
    .map(trimSlashes)
    .filter(
      (path) =>
        /\.ya?ml$/i.test(path) &&
        (wholeSource || inChart(path)) &&
        path !== `${root}/values.yaml` &&
        path !== `${root}/${CHART_FILE}` &&
        !path.startsWith(`${root}/templates/`)
    )
    .sort(
      (a, b) =>
        // The chart's own files first, so widening the search adds to the list
        // rather than burying what it started with.
        Number(inChart(b)) - Number(inChart(a)) || a.localeCompare(b)
    );
}

function trimSlashes(path: string): string {
  return path.replace(/^\/+/, '');
}
